"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cn } from "@/lib/utils"
import { type VariantProps } from "class-variance-authority"
import { Icon } from "./icon";
import {
  FieldTriggerChevron,
  FieldTriggerLabel,
  fieldTriggerClasses,
  fieldTriggerVariants,
} from "./field-trigger";

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

interface SelectTriggerProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
  VariantProps<typeof fieldTriggerVariants> {
  label?: string;
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, label, variant, inputSize, id: idProp, "aria-invalid": ariaInvalid, ...props }, ref) => {
  const reactId = React.useId();
  const triggerId = idProp !== undefined ? idProp : reactId;
  const hasLabel = label !== undefined && label !== "";
  const isError = variant === "error" || ariaInvalid === true;

  const trigger = (
    <SelectPrimitive.Trigger
      ref={ref}
      id={triggerId}
      aria-invalid={ariaInvalid}
      className={fieldTriggerClasses(
        { variant, inputSize },
        cn("[&>span]:max-h-[80px] [&>span]:overflow-y-auto", className),
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <FieldTriggerChevron />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );

  if (hasLabel === false) {
    return trigger;
  }

  // Label renders as a plain block label ABOVE the trigger (Cohort spec
  // form pattern — no floating animation).
  return (
    <div className="flex w-full flex-col gap-1.5">
      <FieldTriggerLabel htmlFor={triggerId} isError={isError}>
        {label}
      </FieldTriggerLabel>
      {trigger}
    </div>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1 text-on-surface-variant",
      className
    )}
    {...props}
  >
    <Icon icon="lucide:chevron-up" size={16} />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1 text-on-surface-variant",
      className
    )}
    {...props}
  >
    <Icon icon="lucide:chevron-down" size={16} />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-[var(--z-modal)] max-h-96 min-w-[8rem] overflow-hidden rounded-xl border border-outline-variant bg-background p-1 text-popover-foreground shadow-[var(--elev-3)] outline-none",
        position === "popper" && "min-w-[var(--radix-select-trigger-width)]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:animate-none",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 type-label-s text-on-surface-variant",
      className
    )}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex h-8 w-full cursor-default select-none items-center gap-2 rounded-md py-1.5 pl-8 pr-2 type-label-l text-foreground outline-none transition-colors duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none",
      "data-[highlighted]:bg-foreground/8 data-[highlighted]:text-accent-foreground",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-60",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex size-4 items-center justify-center text-on-surface-variant">
      <SelectPrimitive.ItemIndicator>
        <Icon icon="lucide:check" size={16} />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-outline-variant", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
